<template>
  <section v-if="isGetRequestPending" class="create-or-update-pipeline">
    <p-loading />
  </section>

  <section v-else-if="isGetRequestFailed" class="create-or-update-pipeline">
    <p>Failed to load this pipeline. Please, try again later or contact our development team.</p>
  </section>

  <div v-else class="create-or-update-pipeline">
    <div class="header">
      <Button color="primary" @click="$router.push('/pipelines')">Back</Button>
    </div>
    <template v-if="isEditing">
      <div class="pipeline-title-row">
        <TextField v-model="title" label="Pipeline Title" />
        <TextField v-model="priority" label="Priority" type="number" />
        <PipelineSelector v-model="dependsOn" :current-pipeline-id="$route.params.id" />
      </div>
      <div class="pipeline-meta-row">
        <Multiselect v-model="tags" :multiple="true" :taggable="true" label="Tags" placeholder="" tag-placeholder="" @tag="tagAdded" />
        <Checkbox v-model="autoSchedule" label="Auto schedule" />
      </div>
      <AccordionItem :is-expanded="expandedSections.includes('filters')" title="Filters" @toggle="toggleExpandedSections('filters')">
        <Filters :value="filters" :is-editing="isEditing" @input="onFiltersInput" />
      </AccordionItem>
      <AccordionItem :is-expanded="expandedSections.includes('action')" title="Actions" @toggle="toggleExpandedSections('action')">
        <Actions v-if="!isGetRequestPending" v-model="actions" :is-editing="isEditing" />
      </AccordionItem>

      <div class="footer">
        <Button color="primary" @click="savePipeline">Save</Button>
        <Button color="secondary" @click="cancelEditing">Cancel</Button>
      </div>
    </template>
    <template v-else>
      <div class="pipeline-summary">
        <div class="pipeline-summary-title">
          <h2>{{ title }}</h2>
          <span class="status-label" :class="isActive ? 'active' : 'paused'">{{ isActive ? 'Running' : 'Paused' }}</span>
          <Button variant="text" @click="isEditing = true">
            <MdIcon name="pencil" />
          </Button>
        </div>
        <dl class="pipeline-summary-details">
          <div>
            <dt>Priority</dt>
            <dd>{{ priority }}</dd>
          </div>
          <div>
            <dt>Version</dt>
            <dd>{{ version }}</dd>
          </div>
          <div>
            <dt>Auto schedule</dt>
            <dd>{{ autoSchedule ? 'Yes' : 'No' }}</dd>
          </div>
          <div v-if="dependsOnPipeline">
            <dt>Depends on</dt>
            <dd>{{ dependsOnPipeline.title }} - Age: {{ dependsOn.age }} days</dd>
          </div>
          <div></div>
          <div v-if="tags.length">
            <dt>Tags</dt>
            <dd><p-tag-collection :value="tags" /></dd>
          </div>
        </dl>
        <div class="pipeline-summary-actions">
          <Button variant="text" @click="$router.push(`/pipelines/${$route.params.id}/history`)">
            <MdIcon name="history" size="sm" /> View History
          </Button>
          <Button @click="isManualSchedulePopupOpen = true">Schedule Manually</Button>
          <Button @click="incrementVersion">Increment version</Button>
          <Button :color="isActive ? 'secondary' : 'primary'" :disabled="isActivateRequestPending" @click="toggleActive">{{
            isActive ? 'Deactivate' : 'Activate'
          }}</Button>
          <Button color="secondary" @click="confirmDelete">Delete</Button>
        </div>
      </div>
      <AccordionItem :is-expanded="expandedSections.includes('filters')" title="Filters" @toggle="toggleExpandedSections('filters')">
        <Filters :value="filters" :is-editing="isEditing" />
      </AccordionItem>
      <AccordionItem :is-expanded="expandedSections.includes('action')" title="Actions" @toggle="toggleExpandedSections('action')">
        <Actions v-model="actions" :is-editing="isEditing" />
      </AccordionItem>

      <ScheduleManually v-if="isManualSchedulePopupOpen" @schedule="handleManualSchedule" @close="isManualSchedulePopupOpen = false" />
    </template>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import Filters from './Filters.vue';
import Button from '@/components/common/Button.vue';
import TextField from '@/components/common/TextField.vue';
import MdIcon from '@/components/common/MdIcon.vue';
import Loading from '@/components/common/Loading.vue';
import Multiselect from '@/components/common/Multiselect.vue';
import TagCollection from '@/components/common/TagCollection.vue';
import Checkbox from '@/components/common/Checkbox.vue';
import PipelineSelector from './PipelineSelector.vue';
import ScheduleManually from './ScheduleManually.vue';
import AccordionItem from './AccordionItem.vue';
import Actions from './Actions.vue';

const NEW_PIPELINE_ID = 'new';
const DEFAULT_PRIORITY = 10;
const createBlankAction = () => ({
  promptOptions: null,
  documentOptions: null,
  mapPromptOptions: null,
  value: null,
  field: null
});

export default {
  name: 'CreateOrUpdatePipeline',
  components: {
    Filters,
    Button,
    TextField,
    MdIcon,
    'p-loading': Loading,
    Multiselect,
    'p-tag-collection': TagCollection,
    Checkbox,
    PipelineSelector,
    ScheduleManually,
    AccordionItem,
    Actions
  },
  data() {
    return {
      title: 'New Pipeline',
      isActive: false,
      isEditing: this.$route.params.id === NEW_PIPELINE_ID,
      filters: [],
      tags: [],
      actions: [createBlankAction()],
      isManualSchedulePopupOpen: false,
      priority: DEFAULT_PRIORITY,
      version: 1,
      filtersChanged: false,
      expandedSections: [],
      autoSchedule: true,
      dependsOn: {
        pipelineId: null,
        age: null
      }
    };
  },
  computed: {
    ...mapState({
      pipelines: state => state.pipelines.collection,
      pipeline: state => state.pipelines.item,
      isGetRequestPending: state => state.pipelines.isGetRequestPending,
      isGetRequestFailed: state => state.pipelines.isGetRequestFailed,
      isActivateRequestPending: state => state.pipelines.isActivateRequestPending
    }),
    dependsOnPipeline() {
      return this.pipelines.find(pipeline => pipeline._id === this.dependsOn.pipelineId) || null;
    },
    isNewPipeline() {
      return this.$route.params.id === NEW_PIPELINE_ID;
    }
  },
  watch: {
    '$route.params.id'() {
      this.initialize();
    }
  },
  async created() {
    await this.initialize();
  },
  methods: {
    async initialize() {
      const promises = [this.$store.dispatch('pipelines/getSettings'), this.$store.dispatch('pipelines/getCollection')];

      if (!this.isNewPipeline) {
        promises.push(this.loadPipeline());
      }

      try {
        await Promise.all(promises);
      } catch (e) {
        // getById failures surface via isGetRequestFailed above; settings/collection failures are logged by the store
      }
    },
    toggleExpandedSections(section) {
      if (this.expandedSections.includes(section)) {
        this.expandedSections = this.expandedSections.filter(s => s !== section);
      } else {
        this.expandedSections.push(section);
      }
    },
    tagAdded(tag) {
      if (!Array.isArray(this.tags)) {
        this.tags = [];
      }

      if (this.tags.includes(tag)) {
        return;
      }
      this.tags.push(tag);
    },
    async savePipeline() {
      const parsedPriority = parseInt(this.priority, 10);
      const pipeline = {
        title: this.title.trim(),
        isActive: this.isActive,
        filters: this.filters,
        tags: this.tags,
        autoSchedule: this.autoSchedule,
        dependsOn: this.dependsOn,
        priority: Number.isNaN(parsedPriority) ? DEFAULT_PRIORITY : parsedPriority,
        actions: this.actions.map(action => ({ ...action })),
        ...(this.isNewPipeline ? {} : { incrementVersion: this.filtersChanged })
      };

      if (!pipeline.title) {
        this.$toast.error({
          title: 'Cannot save pipeline',
          message: 'Please enter a title.'
        });
        return;
      }

      const emptyFilters = !pipeline.filters || pipeline.filters.length === 0;
      const hasEmptyFilterGroups = pipeline.filters.some(filterGroup => !filterGroup.filters || filterGroup.filters.length === 0);
      if (!emptyFilters && hasEmptyFilterGroups) {
        this.$toast.error({
          title: 'Cannot save pipeline',
          message: 'Please add at least one filter.'
        });
        return;
      }

      if (!pipeline.actions.length) {
        this.$toast.error({
          title: 'Cannot save pipeline',
          message: 'Please add at least one action.'
        });
        return;
      }

      for (let index = 0; index < pipeline.actions.length; index += 1) {
        const action = pipeline.actions[index];
        if (action.promptOptions && !action.promptOptions.promptId) {
          this.$toast.error({
            title: 'Cannot save pipeline',
            message: `Please select a prompt for action ${index + 1}.`
          });
          return;
        }

        if (action.documentOptions && (!action.documentOptions.promptId || !action.documentOptions.fileName)) {
          this.$toast.error({
            title: 'Cannot save pipeline',
            message: `Please select a prompt and enter a document name for action ${index + 1}.`
          });
          return;
        }

        if (action.mapPromptOptions && (!action.mapPromptOptions.promptId || !action.mapPromptOptions.itemsFrom)) {
          this.$toast.error({
            title: 'Cannot save pipeline',
            message: `Please select a prompt and an items source for action ${index + 1}.`
          });
          return;
        }

        const isSetFieldAction = !action.promptOptions && !action.documentOptions && !action.mapPromptOptions;
        if (isSetFieldAction && (!action.field || action.value === null || action.value === undefined || action.value === '')) {
          this.$toast.error({
            title: 'Cannot save pipeline',
            message: `Please select a field and value for action ${index + 1}.`
          });
          return;
        }
      }

      const lock = this.$lock();
      try {
        if (!this.isNewPipeline) {
          pipeline.id = this.$route.params.id;
          await this.$store.dispatch('pipelines/update', pipeline);
        } else {
          const created = await this.$store.dispatch('pipelines/create', pipeline);
          this.$router.push(`/pipelines/${created._id}`);
        }
        this.isEditing = false;
        this.filtersChanged = false;
        this.$toast.success({
          title: 'Pipeline saved',
          message: `Pipeline '${pipeline.title}' was saved.`
        });
      } catch (e) {
        this.$toast.error({
          title: 'Failed to save pipeline',
          message: 'Please, try again later or contact our development team.'
        });
      } finally {
        lock.release();
      }
    },
    async loadPipeline() {
      const pipeline = await this.$store.dispatch('pipelines/getById', this.$route.params.id);
      if (pipeline) {
        this.title = pipeline.title;
        this.isActive = pipeline.isActive;
        this.filters = pipeline.filters;
        this.tags = pipeline.tags || [];
        this.autoSchedule = pipeline.autoSchedule !== false;
        this.priority = pipeline.priority || DEFAULT_PRIORITY;
        this.version = pipeline.version || 1;
        if (Array.isArray(pipeline.actions)) {
          this.actions = pipeline.actions.length ? pipeline.actions.map(action => ({ ...action })) : [createBlankAction()];
        } else {
          this.actions = pipeline.actions ? [{ ...pipeline.actions }] : [createBlankAction()];
        }
        this.dependsOn = pipeline.dependsOn || {
          pipelineId: null,
          age: null
        };
      }
    },
    async handleManualSchedule(scheduleIds) {
      this.isManualSchedulePopupOpen = false;
      const lock = this.$lock();
      try {
        await this.$store.dispatch('pipelines/scheduleManually', { pipelineId: this.$route.params.id, ids: scheduleIds });
        this.$toast.success({
          title: 'Schedule started',
          message: 'The selected applications were scheduled for processing.'
        });
      } catch (e) {
        this.$toast.error({
          title: 'Failed to schedule',
          message: 'Please, try again later or contact our development team.'
        });
      } finally {
        lock.release();
      }
    },
    async incrementVersion() {
      const confirm = await this.$confirm({
        title: 'Increment version?',
        message: `Are you sure you want to increment? This will schedule all applications matching the filters for processing.`,
        confirm: 'Increment'
      });

      if (!confirm) {
        return;
      }

      const lock = this.$lock();
      try {
        await this.$store.dispatch('pipelines/update', { id: this.$route.params.id, incrementVersion: true });
        await this.loadPipeline();
      } catch (e) {
        this.$toast.error({
          title: 'Failed to increment version',
          message: 'Please, try again later or contact our development team.'
        });
      } finally {
        lock.release();
      }
    },
    async confirmDelete() {
      const confirm = await this.$confirm({
        title: 'Delete pipeline?',
        message: `Are you sure you want to delete '${this.title}'?\n\nThis will cancel pending runs and cannot be undone.`,
        confirm: 'Delete'
      });

      if (!confirm) {
        return;
      }

      const lock = this.$lock();
      try {
        await this.$store.dispatch('pipelines/delete', this.$route.params.id);
        this.$toast.success({
          title: 'Pipeline deleted',
          message: `Pipeline '${this.title}' was successfully deleted.`
        });
        this.$router.push('/pipelines');
      } catch (e) {
        this.$toast.error({
          title: 'Failed to delete pipeline',
          message: 'Please, try again later or contact our development team.'
        });
      } finally {
        lock.release();
      }
    },
    async toggleActive() {
      const confirm = await this.$confirm({
        title: `${this.isActive ? 'Deactivate' : 'Activate'} pipeline?`,
        message: `Are you sure you want to ${this.isActive ? 'deactivate' : 'activate'} this pipeline? This will ${
          this.isActive ? 'stop' : 'start'
        } processing applications matching the filters.`,
        confirm: this.isActive ? 'Deactivate' : 'Activate'
      });

      if (!confirm) {
        return;
      }

      const action = this.isActive ? 'deactivate' : 'activate';
      const lock = this.$lock();
      try {
        await this.$store.dispatch(`pipelines/${action}`, { pipelineId: this.$route.params.id });
        await this.loadPipeline();
      } catch (e) {
        this.$toast.error({
          title: `Failed to ${action}`,
          message: 'Please, try again later or contact our development team.'
        });
      } finally {
        lock.release();
      }
    },
    onFiltersInput(newFilters) {
      this.filtersChanged = true;
      this.filters = newFilters;
    },
    async cancelEditing() {
      if (this.isNewPipeline) {
        this.$router.push('/pipelines');
        return;
      }
      await this.loadPipeline();
      this.isEditing = false;
    }
  }
};
</script>
<style lang="scss" scoped>
.create-or-update-pipeline {
  padding: 20px;
  width: 100%;

  .header {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 20px;
  }

  .pipeline-title-row {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    margin-bottom: 20px;
  }

  .pipeline-meta-row {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr max-content;
    gap: 10px;
    margin-bottom: 20px;
  }

  .pipeline-summary {
    background-color: var(--theme-surface);
    border: 1px solid var(--theme-highlight);
    border-radius: 4px;
    padding: 1.25rem 1.5rem;
    margin-bottom: 20px;

    .pipeline-summary-title {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.75rem;

      h2 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 700;
        color: var(--theme-on-surface);
      }
    }

    .status-label {
      display: inline-flex;
      line-height: 1;
      height: 1rem;
      justify-content: center;
      align-items: center;
      border-radius: 10%;
      padding: 0.2rem 0.4rem;
      font-size: 0.7rem;
      font-weight: 700;
      letter-spacing: 0.02em;
      text-transform: uppercase;

      &.active {
        color: var(--theme-on-success);
        background-color: var(--theme-success);
      }

      &.paused {
        color: var(--theme-on-background-accent);
        background-color: var(--theme-highlight);
      }
    }

    .pipeline-summary-details {
      display: flex;
      flex-wrap: wrap;
      gap: 1.5rem;
      margin: 0 0 1rem;

      dt {
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        color: var(--theme-on-background-accent);
        margin-bottom: 0.2rem;
      }

      dd {
        margin: 0;
        font-size: 0.9rem;
        color: var(--theme-on-surface);
      }
    }

    .pipeline-summary-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
  }

  .footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
  }
}
</style>
